<script lang="ts" setup>
import { canRankFeatures } from '@beeboard/common';
import { renderMarkdown } from '@/composables/useMarkdown';
import { useFeatureRankingStore } from '@/store/featureRankingStore';
import { useProfileStore } from '@/store/profileStore';
import Dialog from '@/ui/dialogs/AppDialog.vue';
import { formatDate } from '@/utils/helpers/various';
import type { FeatureItem } from '@beeboard/common';

const dateOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  timeZone: 'utc',
};

const featureRankingStore = useFeatureRankingStore();
const profileStore = useProfileStore();

const isVisible = computed(() => {
  const hasRole = canRankFeatures(profileStore.profile.roles);

  return hasRole && featureRankingStore.shouldShowPopup;
});

const session = computed(() => featureRankingStore.session);

const rankingList = ref<FeatureItem[]>([]);

watch(
  () => session.value,
  (newSession) => {
    if (newSession) {
      rankingList.value = [...newSession.features];
    }
  },
  { immediate: true },
);

const expandedIds = ref(new Set<string>());

function toggleDescription(id: string) {
  if (expandedIds.value.has(id)) {
    expandedIds.value.delete(id);
  } else {
    expandedIds.value.add(id);
  }
}

const draggedIndex = ref<number | null>(null);
const dragOverIndex = ref<number | null>(null);
const submitting = ref(false);

function onDragStart(index: number) {
  draggedIndex.value = index;
}

function onDragOver(event: DragEvent, index: number) {
  event.preventDefault();
  dragOverIndex.value = index;
}

function onDrop(index: number) {
  if (draggedIndex.value === null || draggedIndex.value === index) {
    draggedIndex.value = null;
    dragOverIndex.value = null;

    return;
  }

  const items = [...rankingList.value];
  const [moved] = items.splice(draggedIndex.value, 1);
  items.splice(index, 0, moved);
  rankingList.value = items;

  draggedIndex.value = null;
  dragOverIndex.value = null;
}

function onDragEnd() {
  draggedIndex.value = null;
  dragOverIndex.value = null;
}

function moveUp(index: number) {
  if (index === 0) {
    return;
  }
  const items = [...rankingList.value];
  [items[index - 1], items[index]] = [items[index], items[index - 1]];
  rankingList.value = items;
}

function moveDown(index: number) {
  if (index === rankingList.value.length - 1) {
    return;
  }
  const items = [...rankingList.value];
  [items[index], items[index + 1]] = [items[index + 1], items[index]];
  rankingList.value = items;
}

async function submitRanking() {
  submitting.value = true;
  try {
    const featureIds = rankingList.value.map((f) => f.id);
    await featureRankingStore.submitRanking(featureIds);
    featureRankingStore.dismissPopup();
  } finally {
    submitting.value = false;
  }
}

function dismiss() {
  featureRankingStore.dismissPopup();
}

const sessionRequested = ref(false);

watch(
  () => profileStore.profile.roles,
  async (roles) => {
    if (!sessionRequested.value && canRankFeatures(roles)) {
      sessionRequested.value = true;
      await featureRankingStore.loadSession();
    }
  },
  { immediate: true },
);
</script>

<template>
  <Dialog
    v-model="isVisible"
    :title="$t('featureRanking.title')"
    :subtitle="$t('featureRanking.popupSubtitle')"
    :closable="false"
  >
    <template #content>
      <div class="ranking-list">
        <div
          v-for="(feature, index) in rankingList"
          :key="feature.id"
          class="ranking-item"
          :class="{
            'ranking-item--drag-over': dragOverIndex === index,
            'ranking-item--dragging': draggedIndex === index,
          }"
          draggable="true"
          @dragstart="onDragStart(index)"
          @dragover="onDragOver($event, index)"
          @drop="onDrop(index)"
          @dragend="onDragEnd"
        >
          <div class="ranking-item__drag-handle">
            <UiIcon name="GripVertical" :size="16" />
          </div>

          <div
            class="ranking-item__body"
            :class="{
              'ranking-item__body--expanded': expandedIds.has(feature.id),
            }"
          >
            <div class="ranking-item__title">{{ feature.title }}</div>
            <div class="ranking-item__meta">
              <span class="ranking-item__devops">#{{ feature.devops_id }}</span>
              <span v-if="feature.effort"
                >{{ $t('featureRanking.effort') }}:
                {{ feature.effort }}/10</span
              >
              <span v-if="feature.feature_date">{{
                formatDate(feature.feature_date, dateOptions)
              }}</span>
              <button
                v-if="feature.description"
                class="ranking-item__toggle"
                @click.stop="toggleDescription(feature.id)"
              >
                {{
                  expandedIds.has(feature.id)
                    ? $t('featureRanking.hideDescription')
                    : $t('featureRanking.showDescription')
                }}
              </button>
            </div>
            <div
              v-if="feature.description && expandedIds.has(feature.id)"
              class="ranking-item__description markdown-content"
              v-html="renderMarkdown(feature.description)"
            />
          </div>

          <div class="ranking-item__arrows">
            <button
              class="ranking-item__btn"
              :disabled="index === 0"
              @click.stop="moveUp(index)"
            >
              <UiIcon name="ChevronUp" :size="14" />
            </button>
            <button
              class="ranking-item__btn"
              :disabled="index === rankingList.length - 1"
              @click.stop="moveDown(index)"
            >
              <UiIcon name="ChevronDown" :size="14" />
            </button>
          </div>
        </div>
      </div>
    </template>

    <template #actions>
      <div class="ranking-actions">
        <UiButton @click="dismiss">
          {{ $t('featureRanking.skipForToday') }}
        </UiButton>
        <UiButton
          variant="primary"
          :disabled="submitting"
          @click="submitRanking"
        >
          {{
            submitting
              ? $t('featureRanking.saving')
              : $t('featureRanking.submitRanking')
          }}
        </UiButton>
      </div>
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
.ranking-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  max-height: 60vh;
  overflow-y: auto;
  padding: var(--space-8) 0;
}

.ranking-item {
  display: flex;
  align-items: flex-start;
  gap: var(--space-8);
  padding: var(--space-10) var(--space-12);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--border-radius-6);
  background: var(--color-gray-0);
  cursor: grab;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;

  &:hover {
    border-color: var(--color-gray-300);
    background: var(--color-gray-50);
  }

  &--drag-over {
    border-color: var(--color-primary);
    border-style: dashed;
  }

  &--dragging {
    opacity: 0.3;
  }

  &__drag-handle {
    flex-shrink: 0;
    color: var(--color-gray-300);
    margin-top: var(--space-2);
    cursor: grab;
  }

  &__body {
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }

  &__title {
    font-weight: var(--font-weight-medium);
    font-size: var(--font-size-14);
    color: var(--color-gray-900);
  }

  &__body:not(.ranking-item__body--expanded) &__title {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__meta {
    display: flex;
    gap: var(--space-10);
    font-size: var(--font-size-11);
    color: var(--color-gray-400);
    margin-top: var(--space-2);
    align-items: center;
  }

  &__toggle {
    background: none;
    border: none;
    cursor: pointer;
    font-size: var(--font-size-11);
    color: var(--color-primary);
    padding: 0;
    white-space: nowrap;

    &:hover {
      text-decoration: underline;
    }
  }

  &__description {
    font-size: var(--font-size-12);
    color: var(--color-gray-600);
    margin-top: var(--space-8);
    padding: var(--space-8) var(--space-10);
    background: var(--color-gray-50);
    border-radius: var(--border-radius-4);
    max-height: px(200);
    overflow-y: auto;

    :deep(p) {
      margin: 0 0 var(--space-6) 0;
    }

    :deep(ul),
    :deep(ol) {
      margin: 0 0 var(--space-6) 0;
      padding-left: var(--space-16);
    }

    :deep(strong) {
      font-weight: var(--font-weight-bold);
    }

    :deep(code) {
      background-color: var(--color-gray-200);
      padding: px(1) px(3);
      border-radius: var(--border-radius-2);
      font-size: var(--font-size-11);
    }

    :deep(p:last-child) {
      margin-bottom: 0;
    }
  }

  &__arrows {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    margin-top: px(1);
  }

  &__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: px(22);
    height: px(18);
    border-radius: px(3);
    background: transparent;
    border: none;
    cursor: pointer;
    color: var(--color-gray-400);

    &:hover:not(:disabled) {
      background: var(--color-gray-100);
      color: var(--color-gray-700);
    }

    &:disabled {
      opacity: 0.2;
      cursor: default;
    }
  }
}

.ranking-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-8);
  margin-top: var(--space-16);
}
</style>
